import React, { useCallback, useContext, useEffect, useState } from 'react'
import {
  CurrencyEnum,
  GamePlayData,
  SkillEnum,
  SkillStates,
  Stats,
  UpgradeEnum,
  WaveResult,
  JobEnum,
  ActivityEnum,
  LocationEnum,
  QuestEnum,
  SkillResults,
  TimelineItem,
  EquipmentEnum,
  GameSpeedTier
} from '../types'
import {
  calculateSkillLevel,
  getOverallSkillLevel,
  isProductionBuild,
  loadGameDataLocal,
  saveGameDataLocal,
  getBrowserInfo,
  getEnergyFitnessMultiplier,
  stateMigrations,
  isDemo,
  calculateOfflineStoke,
  getRealDurationMinutes,
  getOverallSkillMultiplier
} from '../utils'
import { UPGRADES } from '../constants/upgrades'
import { generateSurfSession, getSessionLength } from '../engine/generateSurfSession'
import {
  SURF_MINUTES_PER_ENERGY,
  TICK_INTERVAL_MILLISECONDS,
  BASE_ENERGY_REGENERATION_PER_HOUR,
  WORK_MINUTES_PER_ENERGY,
  MIN_ENERGY_FOR_ACTIVITY_START,
  ENERGY_PER_FITNESS_LEVEL,
  BASE_GAME_MINUTES_PER_TICK,
  SURF_GAME_SPEED_SLOWDOWN,
  GAME_SAVE_INTERVAL,
  SKILL_LEVEL_CAP,
  BASE_MAX_ENERGY
} from '../constants/game'
import { JOBS } from '@/constants/jobs'
import SKILL_TREE from '@/constants/skillTree'
import { getStory } from '@/constants/story'
import { SURF_SPOTS } from '@/constants/surfSpots'
import LOCATIONS from '@/constants/locations'
import { toastSkillIncrease, toastOverallSkillIncrease } from '../components/shared/CommonToasts'
import { useAuth } from './authContext'
import { getServerTime } from '@/utils'
import { supabase } from '@/utils/supabase'
import { performAutomations } from '../engine/automations'
import { completeQuestAsAutomation } from '../engine/questCompletion'
import { applyMultipliers, removeMultipliers } from '../engine/multipliers'
import { calculateEquipmentFees, getEquipmentChangeData } from '@/engine/equipment'
import { STOKE_GAME_SPEED_COSTS } from '@/constants/stoke'

export interface IGameplayContext {
  gameData: GamePlayData
  gameSpeed: number
  setGameSpeed: React.Dispatch<React.SetStateAction<number>>
  setGameData: React.Dispatch<React.SetStateAction<GamePlayData>>
  incrementCurrency: (name: CurrencyEnum, value: number) => void
  purchaseUpgrade: (name: UpgradeEnum) => void
  darkMode: boolean
  setDarkMode: React.Dispatch<React.SetStateAction<boolean>>
  changeLocation: (location: LocationEnum) => void
  updateSkills: (skillsToUpdate: SkillResults) => void
  changeLocationSpot: (spot: string) => void
  startJob: (jobKey: string) => void
  updateJobHours: (jobKey: string, hours: number) => void
  setCurrentActivity: (activity: ActivityEnum) => void
  completeQuest: (questKey: QuestEnum) => void
  showStoryModal: boolean
  setShowStoryModal: (show: boolean) => void
  addTimelineItem: (item: TimelineItem) => void
  findNextStoryItem: () => TimelineItem | null
  getGameMinutesPerTick: () => number
  saveToCloud: () => Promise<boolean>
  loadFromCloud: () => Promise<boolean>
  changeEquipment: (type: EquipmentEnum, newEquipment: UpgradeEnum | UpgradeEnum[] | undefined) => void
  earnStoke: (amount: number) => void
  setGameSpeedWithStoke: (speed: GameSpeedTier) => void
  unlockGameSpeed: (speed: GameSpeedTier) => void
  isAutoplayEnabled: boolean
  setIsAutoplayEnabled: (isAutoplayEnabled: boolean) => void
}

const GameplayContext = React.createContext<IGameplayContext | undefined>(undefined)

function getUpdatedSkills(skills: SkillStates, skillsToUpdate: SkillResults): SkillStates {
  const updatedSkills: any = {}

  // Deep clone the original skills first
  for (const key in skills) {
    const k = key as SkillEnum
    updatedSkills[k] = {
      ...skills[k],
      appliedMultipliers: [...(skills[k].appliedMultipliers || [])]
    }
  }

  // Update with new skill points
  for (const key in skillsToUpdate) {
    const k: SkillEnum = key as SkillEnum
    if (!updatedSkills[k]) {
      updatedSkills[k] = {
        points: 0,
        level: 0,
        appliedMultipliers: [],
        totalMultiplier: 1
      }
    }
    updatedSkills[k] = {
      ...updatedSkills[k],
      points: updatedSkills[k].points + (skillsToUpdate[k]?.points || 0)
    }
    updatedSkills[k].level = calculateSkillLevel(k, updatedSkills[k].points)
  }

  return updatedSkills
}

const updateSessionStatsData = (stats: Stats) => {
  const updatedStats: Stats = { ...stats }
  updatedStats.surfSessionsTotal = stats.surfSessionsTotal + 1
  return updatedStats
}

const getStartSurfSessionData = (data: GamePlayData): GamePlayData => {
  const sessionLength = getSessionLength(data)
  const sessionResults = generateSurfSession(data.currentLocation, data, sessionLength)
  // Update session stats
  const updatedStats = updateSessionStatsData(data.stats)
  const updatedLocationStats = {
    ...data.locationStats,
    [data.currentLocation]: updateSessionStatsData(data.locationStats[data.currentLocation])
  }
  const surfSessionCost = LOCATIONS[data.currentLocation].surfSessionCost
  return {
    ...data,
    surfSession: {
      computedSessionData: sessionResults.waveResults,
      appliedSessionData: [],
      totalTime: sessionLength,
      elapsedTime: 0,
      isActive: true,
      sessionInputs: sessionResults.sessionInputs,
      critProbabilityComboMultiplier: 1
    },
    stats: updatedStats,
    locationStats: updatedLocationStats,
    currencies: {
      ...data.currencies,
      money: {
        ...data.currencies.money,
        amount: data.currencies.money.amount - surfSessionCost
      }
    }
  }
}

function getUpdatedWaveStats(stats: Stats, waveResult: WaveResult): Stats {
  const updatedStats: Stats = { ...stats }
  if (waveResult.rideResult === 'ride') {
    updatedStats.wavesTotal += 1
    updatedStats.ridingWaveSecondsTotal += waveResult.waveInputs.waveTime
    updatedStats.criticalMovesTotal += Object.values(waveResult.skills).filter((s) => s.isCritical).length
    updatedStats.respectTotal += Object.values(waveResult.skills).reduce((sum, skill) => sum + skill.respect, 0)
  } else if (waveResult.rideResult === 'wipeout') {
    updatedStats.wipeoutsTotal += 1
  }
  updatedStats.waitingForWaveMinutesTotal += waveResult.waveInputs.waitingForWaveTime
  updatedStats.surfSessionMinutesTotal += waveResult.waveInputs.waveTime / 60 + waveResult.waveInputs.waitingForWaveTime
  updatedStats.injuriesTotal += waveResult.hazards.filter((h) => h.type === 'injury').length
  updatedStats.damageTotal += waveResult.hazards.filter((h) => h.type === 'damage').length
  return updatedStats
}

function getUpdatedTotalSkillPoints(currentTotal: number, skillsToUpdate: SkillResults): number {
  return currentTotal + Object.values(skillsToUpdate).reduce((sum, skill) => sum + skill.points, 0)
}

const incrementUsername = (username: string): string => {
  const parts = username.split('_')

  // If username has 4 parts (3 underscores), add _s1
  if (parts.length === 4) {
    return `${username}_s1`
  }

  // If username has 5 parts and ends with _sN
  if (parts.length === 5 && parts[4].startsWith('s')) {
    const currentNumber = parseInt(parts[4].substring(1))
    if (!isNaN(currentNumber)) {
      parts[4] = `s${currentNumber + 1}`
      return parts.join('_')
    }
  }

  // Fallback: append _s1 if pattern doesn't match
  return `${username}_s1`
}

const firstLoadGameData = loadGameDataLocal()

const GameplayProvider = ({ children }: React.PropsWithChildren<object>) => {
  const [gameData, setGameData] = useState<GamePlayData>(firstLoadGameData)
  const [baseGameSpeed, setBaseGameSpeed] = useState<number>(1)
  const [gameSpeed, setGameSpeed] = useState<number>(1)
  const [acceleratedSpeed, setAcceleratedSpeed] = useState<GameSpeedTier>(1)
  const [isAccelerated, setIsAccelerated] = useState<boolean>(false)
  const [darkMode, setDarkMode] = useState(false)
  const [gameLoopInterval, setGameLoopInterval] = useState<NodeJS.Timer>()
  const [gameSaveInterval, setGameSaveInterval] = useState<NodeJS.Timer>()
  const [showStoryModal, setShowStoryModal] = useState(false)
  const { user, ensureAnonymousAuth } = useAuth()
  const [isCloudSaving, setIsCloudSaving] = useState(false)
  const [isCloudLoading, setIsCloudLoading] = useState(false)
  const [isAutoplayEnabled, setIsAutoplayEnabled] = useState(false)

  const completeQuest = useCallback((questKey: QuestEnum) => {
    setGameData((prevData) => completeQuestAsAutomation(prevData, questKey, () => setShowStoryModal(true)))
  }, [])

  useEffect(() => {
    setGameData(loadGameDataLocal())
  }, [isDemo()])

  const getGameMinutesPerTick = useCallback(() => {
    return gameSpeed * BASE_GAME_MINUTES_PER_TICK
  }, [gameSpeed])

  useEffect(() => {
    if (gameLoopInterval) {
      clearInterval(gameLoopInterval)
    }
    const gInterval = setInterval(() => {
      setGameData((prevData: GamePlayData) => {
        const minutesPerTick = getGameMinutesPerTick()
        const hoursPerTick = minutesPerTick / 60
        const daysPerTick = hoursPerTick / 24
        const realMinutesPerTick = TICK_INTERVAL_MILLISECONDS / (1000 * 60)
        let newData = { ...prevData, tick: prevData.tick + 1, gameMinutes: prevData.gameMinutes + minutesPerTick }
        // Update stats
        newData.stats.gameDaysTotal = (newData.stats.gameDaysTotal || 0) + daysPerTick
        newData.locationStats[newData.currentLocation].gameDaysTotal =
          (newData.locationStats[newData.currentLocation].gameDaysTotal || 0) + daysPerTick

        // Handle automations
        newData = performAutomations(newData)

        // Consume Stoke if game is accelerated
        if (isAccelerated && acceleratedSpeed > 1) {
          const stokeCost = STOKE_GAME_SPEED_COSTS[acceleratedSpeed]
          const stokeToConsume = stokeCost * realMinutesPerTick

          // Only continue acceleration if there's enough stoke
          if (newData.currencies.stoke.amount >= stokeToConsume) {
            // Deduct stoke
            newData.currencies.stoke.amount = Math.max(0, newData.currencies.stoke.amount - stokeToConsume)
          } else {
            // Not enough stoke, revert to normal speed
            setAcceleratedSpeed(1)
            setIsAccelerated(false)
          }
        }

        // Handle activities
        switch (newData.currentActivity) {
          case 'rest':
            const energyPerTick = BASE_ENERGY_REGENERATION_PER_HOUR * hoursPerTick
            const energyMultiplier = newData.currencies.energy.totalMultiplier || 1
            newData.currencies.energy.amount = Math.min(
              newData.currencies.energy.amount + energyPerTick * energyMultiplier,
              newData.currencies.energy.max || BASE_MAX_ENERGY * SKILL_LEVEL_CAP
            )
            break
          case 'work':
            if (newData.currentJob) {
              const energyConsumed = minutesPerTick / WORK_MINUTES_PER_ENERGY
              newData.currencies.energy.amount = Math.max(newData.currencies.energy.amount - energyConsumed, 0)
              // Calculate and add work and respect income
              const job = JOBS[newData.currentJob]
              if (job) {
                const incomePerTick = job.income * hoursPerTick
                const moneyMultiplier = newData.currencies.money.totalMultiplier || 1
                newData.currencies.money.amount += incomePerTick * moneyMultiplier
                if (job.respectIncome) {
                  const respectPerTick = job.respectIncome * hoursPerTick
                  newData.stats.respectTotal = (newData.stats.respectTotal || 0) + respectPerTick
                  newData.locationStats[newData.currentLocation].respectTotal =
                    (newData.locationStats[newData.currentLocation].respectTotal || 0) + respectPerTick
                }
                newData.jobs[newData.currentJob] = (newData.jobs[newData.currentJob] || 0) + hoursPerTick
              }
            }
            break
          case 'surf':
            if (!newData.surfSession.isActive) {
              newData = getStartSurfSessionData(newData)
            }
            break
        }
        // Check if energy has reached 0 and switch to rest if necessary
        if (newData.currencies.energy.amount <= 0) {
          newData.currencies.energy.amount = 0
          newData.currentActivity = 'rest'
        }

        // Calculate and apply hourly fees from equipment
        const { totalFees } = calculateEquipmentFees(newData)
        const feesPerTick = totalFees * hoursPerTick
        const newMoneyAmount = newData.currencies.money.amount - feesPerTick

        if (newMoneyAmount < 0) {
          // If money would go negative, deactivate all equipment and set money to 0
          newData.currencies.money.amount = 0
          newData.equipment = {
            ...newData.equipment,
            housing: undefined,
            other: newData.equipment.other.filter((item) => UPGRADES[item].hourlyFee === undefined)
          }
        } else {
          newData.currencies.money.amount = newMoneyAmount
        }

        return newData
      })
    }, TICK_INTERVAL_MILLISECONDS)
    setGameLoopInterval(gInterval)

    return () => {
      clearInterval(gameLoopInterval)
    }
  }, [gameSpeed, isAccelerated, acceleratedSpeed])

  useEffect(() => {
    if (!gameSaveInterval && isProductionBuild()) {
      const gameSaveInterval = setInterval(() => {
        setGameData((prevGameData) => {
          saveGameDataLocal(prevGameData)
          saveToCloud().catch(console.error)
          return prevGameData
        })
      }, GAME_SAVE_INTERVAL)
      setGameSaveInterval(gameSaveInterval)
    }
    return () => {
      clearInterval(gameSaveInterval)
    }
  }, [])

  const incrementCurrency = useCallback(
    (key: CurrencyEnum, value: number) => {
      setGameData((data) => ({
        ...data,
        currencies: {
          ...data.currencies,
          [key]: {
            ...data.currencies[key],
            amount: data.currencies[key].max
              ? Math.min(data.currencies[key].amount + value, data.currencies[key].max)
              : data.currencies[key].amount + value
          }
        }
      }))
    },
    [gameData, gameData.tick]
  )

  const changeLocation = useCallback(
    (location: LocationEnum) => {
      setGameData((data) => ({
        ...data,
        currentLocation: location
      }))
    },
    [gameData, gameData.tick]
  )

  const updateSkills = useCallback(
    (skillsToUpdate: SkillResults) => {
      setGameData((data) => {
        const updatedSkills = getUpdatedSkills(data.skills, skillsToUpdate)
        const updatedTotalSkillPoints = getUpdatedTotalSkillPoints(data.currencies.skill.amount, skillsToUpdate)
        const updatedTotalSkillLevel = getOverallSkillLevel(SKILL_TREE, updatedSkills)?.level || 0
        const updatedTotalSkillMultiplier = getOverallSkillMultiplier(updatedTotalSkillLevel)

        // Update skill multiplier
        const existingSkillMultiplier = data.currencies.skill.appliedMultipliers?.find(
          (m) => m.category === 'permanent' && m.sourceCategory === 'currency' && m.sourceName === 'skill'
        )
        if (existingSkillMultiplier) {
          existingSkillMultiplier.multiplier = updatedTotalSkillMultiplier
        }

        // Update energy multiplier based on fitness level
        const fitnessLevel = updatedSkills.fitness.level || 1
        const energyFitnessMultiplier = getEnergyFitnessMultiplier(fitnessLevel)
        const existingEnergyMultiplier = data.currencies.energy.appliedMultipliers?.find(
          (m) => m.category === 'permanent' && m.sourceCategory === 'currency' && m.sourceName === 'energy'
        )
        if (existingEnergyMultiplier) {
          existingEnergyMultiplier.multiplier = energyFitnessMultiplier
        }

        // Update total energy multiplier
        const totalEnergyMultiplier =
          data.currencies.energy.appliedMultipliers?.reduce((total, m) => total * m.multiplier, 1) || 1

        const fitnessMax = (updatedSkills.fitness.level || 1) * ENERGY_PER_FITNESS_LEVEL

        // Check for skill improvements
        const newTimelineItems: TimelineItem[] = []
        for (const skillKey in updatedSkills) {
          const skill = updatedSkills[skillKey as SkillEnum]
          if (skill.level > data.skills[skillKey as SkillEnum].level) {
            newTimelineItems.push({
              type: 'skill',
              contentRef: skillKey as SkillEnum,
              reachedValue: skill.level,
              gameMinutes: gameData.gameMinutes,
              realMinutes: getRealDurationMinutes(gameData.tick)
            })
            toastSkillIncrease(skillKey as SkillEnum, skill.level)
          }
        }
        const currentTotalSkillLevel = getOverallSkillLevel(SKILL_TREE, data.skills)?.level || 0
        if (updatedTotalSkillLevel > currentTotalSkillLevel) {
          newTimelineItems.push({
            type: 'overall_skill',
            contentRef: updatedTotalSkillLevel,
            reachedValue: updatedTotalSkillLevel,
            gameMinutes: gameData.gameMinutes,
            realMinutes: getRealDurationMinutes(gameData.tick)
          })
          toastOverallSkillIncrease(updatedTotalSkillLevel)
        }

        return {
          ...data,
          skills: updatedSkills,
          currencies: {
            ...data.currencies,
            skill: {
              ...data.currencies.skill,
              amount: updatedTotalSkillPoints,
              level: updatedTotalSkillLevel,
              totalMultiplier: updatedTotalSkillMultiplier,
              appliedMultipliers: data.currencies.skill.appliedMultipliers
            },
            energy: {
              ...data.currencies.energy,
              max: fitnessMax,
              totalMultiplier: totalEnergyMultiplier,
              appliedMultipliers: data.currencies.energy.appliedMultipliers
            }
          },
          timeline: [...data.timeline, ...newTimelineItems]
        }
      })
    },
    [gameData, gameData.tick]
  )

  const updateWaveStats = useCallback(
    (waveResult: WaveResult) => {
      setGameData((data) => ({
        ...data,
        stats: getUpdatedWaveStats(data.stats, waveResult),
        locationStats: {
          ...data.locationStats,
          [data.currentLocation]: getUpdatedWaveStats(data.locationStats[data.currentLocation], waveResult)
        }
      }))
    },
    [gameData, gameData.tick]
  )

  const purchaseUpgrade = useCallback((upgradeKey: UpgradeEnum) => {
    setGameData((prevData) => {
      const upgrade = UPGRADES[upgradeKey]
      if (!upgrade || prevData.upgrades[upgradeKey]?.isPurchased || prevData.currencies.money.amount < upgrade.cost) {
        return prevData
      }

      return {
        ...prevData,
        currencies: {
          ...prevData.currencies,
          money: {
            ...prevData.currencies.money,
            amount: prevData.currencies.money.amount - upgrade.cost
          }
        },
        upgrades: {
          ...prevData.upgrades,
          [upgradeKey]: {
            isPurchased: true
          }
        }
      }
    })
  }, [])

  const updateSurfSession = useCallback(() => {
    setGameData((data) => {
      if (!data.surfSession.isActive) return data

      const { computedSessionData, appliedSessionData, elapsedTime, totalTime } = data.surfSession

      const newElapsedTime = elapsedTime + getGameMinutesPerTick()
      const energyUsed = getGameMinutesPerTick() / SURF_MINUTES_PER_ENERGY

      let newAppliedSessionData = [...appliedSessionData]
      let newWaves: WaveResult[] = []

      // Calculate total time including both waiting and wave times
      const totalAppliedTime = newAppliedSessionData.reduce(
        (total, wave) => total + (wave.waveInputs.waitingForWaveTime + wave.waveInputs.waveTime / 60),
        0
      )

      // Check if we should apply next waves
      while (
        newAppliedSessionData.length + newWaves.length < computedSessionData.length &&
        newElapsedTime >=
          totalAppliedTime +
            newWaves.reduce(
              (total, wave) => total + (wave.waveInputs.waitingForWaveTime + wave.waveInputs.waveTime / 60),
              0
            )
      ) {
        const nextWave = computedSessionData[newAppliedSessionData.length + newWaves.length]
        newWaves.push(nextWave)
      }

      // Apply all new waves
      if (newWaves.length > 0) {
        newAppliedSessionData.push(...newWaves)

        // Process all new waves
        for (const wave of newWaves) {
          updateSkills(wave.skills)
          updateWaveStats(wave)

          // Check for severe hazards
          const severeHazard = wave.hazards.find((hazard) => hazard.severity === 'medium' || hazard.severity === 'big')

          if (severeHazard) {
            // Handle hazard consequences
            wave.hazards.forEach((hazard) => {
              incrementCurrency(hazard.consequenceCurrency, -hazard.consequenceCost)
            })

            // Stop the session immediately
            return {
              ...data,
              surfSession: {
                ...data.surfSession,
                appliedSessionData: newAppliedSessionData,
                elapsedTime: newElapsedTime,
                isActive: false
              },
              currentActivity: 'rest'
            }
          }

          // Handle normal hazard consequences if no severe hazards
          wave.hazards.forEach((hazard) => {
            incrementCurrency(hazard.consequenceCurrency, -hazard.consequenceCost)
          })
        }
      }

      incrementCurrency('energy', -energyUsed)

      const isActive = !(data.currentActivity !== 'surf' && data.surfSession.isActive)
      return {
        ...data,
        surfSession: {
          ...data.surfSession,
          appliedSessionData: newAppliedSessionData,
          elapsedTime: newElapsedTime,
          isActive
        }
      }
    })
  }, [incrementCurrency, updateSkills, updateWaveStats])

  useEffect(() => {
    if (gameData.surfSession.isActive) {
      updateSurfSession()
    }
  }, [gameData.tick])

  const changeLocationSpot = useCallback(
    (spot: string) => {
      setGameData((data) => {
        // First remove all existing surf spot multipliers
        let newData = removeMultipliers(data, 'surfSpot', data.currentLocationSpot)

        // Apply new surf spot multipliers
        const spotMultipliers = SURF_SPOTS[spot] || []
        if (spotMultipliers.length > 0) {
          newData = applyMultipliers(newData, spotMultipliers, spot, 'surfSpot')
        }

        return {
          ...newData,
          currentLocationSpot: spot
        }
      })
    },
    [gameData, gameData.tick]
  )

  const startJob = useCallback(
    (jobKey: string) => {
      setGameData((prevData) => ({
        ...prevData,
        currentJob: jobKey as JobEnum
      }))
    },
    [gameData, gameData.tick]
  )

  const updateJobHours = useCallback(
    (jobKey: string, hours: number) => {
      setGameData((prevData) => ({
        ...prevData,
        jobs: {
          ...prevData.jobs,
          [jobKey as JobEnum]: (prevData.jobs[jobKey as JobEnum] || 0) + hours
        }
      }))
    },
    [gameData, gameData.tick]
  )

  const setCurrentActivity = useCallback((activity: ActivityEnum) => {
    setGameData((prevData) => {
      let newData = { ...prevData, currentActivity: activity }

      // Check if we have enough energy to start the activity
      if (activity !== 'rest' && newData.currencies.energy.amount < MIN_ENERGY_FOR_ACTIVITY_START) {
        // If not enough energy, set activity to rest
        newData.currentActivity = 'rest'
        return newData
      }
      return newData
    })
  }, [])

  const addTimelineItem = useCallback((item: TimelineItem) => {
    setGameData((prevData) => ({
      ...prevData,
      timeline: [...prevData.timeline, item]
    }))
  }, [])

  const findLatestStoryItem = useCallback((): TimelineItem | null => {
    const completedStoryItems = gameData.timeline?.filter((tItem) => ['story', 'quest'].includes(tItem.type))
    return completedStoryItems?.length > 0 ? completedStoryItems[completedStoryItems.length - 1] : null
  }, [gameData.timeline])

  const findNextStoryItem = useCallback((): TimelineItem | null => {
    const STORY = getStory()
    const latestStoryItem = findLatestStoryItem()
    const nextStoryIndex = latestStoryItem ? (latestStoryItem.contentRef as number) + 1 : 0

    if (nextStoryIndex >= STORY.length) {
      return null
    }

    const storyElement = STORY[nextStoryIndex]
    return {
      type: storyElement.type,
      contentRef: nextStoryIndex,
      reachedValue: 0,
      gameMinutes: gameData.gameMinutes,
      realMinutes: getRealDurationMinutes(gameData.tick)
    }
  }, [gameData.timeline, gameData.gameMinutes])

  // Initial story check and quest completion check
  useEffect(() => {
    const nextItem = findNextStoryItem()
    if (
      nextItem &&
      !(nextItem.type === 'quest' && !gameData.completedQuests.includes(nextItem.contentRef as QuestEnum)) &&
      !isAutoplayEnabled
    ) {
      setShowStoryModal(true)
    }
  }, [gameData.timeline, gameData.completedQuests])

  // Stoke management functions
  const earnStoke = useCallback((amount: number) => {
    setGameData((prevData) => ({
      ...prevData,
      currencies: {
        ...prevData.currencies,
        stoke: {
          ...prevData.currencies.stoke,
          amount: Math.min(prevData.currencies.stoke.amount + amount, prevData.currencies.stoke.max || 1)
        }
      }
    }))
  }, [])

  const setGameSpeedWithStoke = useCallback(
    (speed: GameSpeedTier) => {
      // Don't do anything if the speed is already active or not unlocked
      if (gameData.currencies.stoke.unlockedLevels?.includes(speed)) {
        if (speed === 1) {
          // Normal speed doesn't consume stoke
          setAcceleratedSpeed(1)
          setIsAccelerated(false)
        } else if (speed !== acceleratedSpeed || !isAccelerated) {
          // Set new accelerated speed
          setAcceleratedSpeed(speed)
          setIsAccelerated(true)
        }
      }
    },
    [gameData.currencies.stoke.unlockedLevels, acceleratedSpeed, isAccelerated]
  )

  const unlockGameSpeed = useCallback((speed: GameSpeedTier) => {
    setGameData((prevData) => {
      if (prevData.currencies.stoke.unlockedLevels?.includes(speed)) {
        return prevData
      }
      return {
        ...prevData,
        currencies: {
          ...prevData.currencies,
          stoke: {
            ...prevData.currencies.stoke,
            unlockedLevels: [...(prevData.currencies.stoke.unlockedLevels || []), speed].sort((a, b) => a - b)
          }
        }
      }
    })
  }, [])

  // Update game speed from settings
  useEffect(() => {
    let newSpeed = baseGameSpeed

    if (isAccelerated) {
      newSpeed = acceleratedSpeed * baseGameSpeed
    }

    if (gameData.currentActivity === 'surf') {
      newSpeed = newSpeed / SURF_GAME_SPEED_SLOWDOWN
    }

    setGameSpeed(newSpeed)
  }, [baseGameSpeed, gameData.currentActivity, isAccelerated, acceleratedSpeed])

  // Update lastSaveTime when saving to cloud
  const saveToCloud = async (): Promise<boolean> => {
    try {
      setIsCloudSaving(true)
      const authUser = await ensureAnonymousAuth()
      const currentTime = await getServerTime() // Use server time

      // Update lastSaveTime in gameData
      setGameData((prevData) => ({
        ...prevData,
        lastSaveTime: currentTime
      }))

      const { error } = await supabase.from('save').upsert(
        {
          id: authUser?.id,
          data: {
            ...gameData,
            lastSaveTime: currentTime,
            browserInfo: getBrowserInfo()
          },
          username: gameData.username,
          updated_at: currentTime
        },
        {
          onConflict: 'id',
          ignoreDuplicates: false
        }
      )

      if (error) {
        console.error('Error saving to cloud:', error)
        return false
      }
      return true
    } catch (error) {
      console.error('Error saving to cloud:', error)
      return false
    } finally {
      setIsCloudSaving(false)
    }
  }

  // Modified load from cloud to process offline stoke
  const loadFromCloud = async (): Promise<boolean> => {
    try {
      setIsCloudLoading(true)
      const authUser = await ensureAnonymousAuth()

      const { data, error } = await supabase.from('save').select('data').eq('id', user?.id).single()

      if (error) throw error
      if (data) {
        const loadedData = stateMigrations(data.data)

        setGameData(loadedData)
        saveGameDataLocal(loadedData)
        return true
      }
      return false
    } catch (error) {
      console.error('Error loading from cloud:', error)
      return false
    } finally {
      setIsCloudLoading(false)
    }
  }

  const changeEquipment = useCallback((type: EquipmentEnum, newEquipment: UpgradeEnum | UpgradeEnum[] | undefined) => {
    setGameData((prevData) => getEquipmentChangeData(prevData, type, newEquipment))
  }, [])

  const value: IGameplayContext = {
    gameData,
    gameSpeed,
    setGameSpeed: setBaseGameSpeed,
    setGameData,
    incrementCurrency,
    purchaseUpgrade,
    darkMode,
    setDarkMode,
    changeLocation,
    updateSkills,
    changeLocationSpot,
    startJob,
    updateJobHours,
    setCurrentActivity,
    completeQuest,
    showStoryModal,
    setShowStoryModal,
    addTimelineItem,
    findNextStoryItem,
    getGameMinutesPerTick,
    saveToCloud,
    loadFromCloud,
    changeEquipment,
    earnStoke,
    setGameSpeedWithStoke,
    unlockGameSpeed,
    isAutoplayEnabled,
    setIsAutoplayEnabled
  }

  return <GameplayContext.Provider value={value}>{children}</GameplayContext.Provider>
}

export function useGameplayContext() {
  const context = useContext(GameplayContext)

  if (typeof context === 'undefined') {
    throw new Error('useGameplayContext must be used within a GameplayContextProvider')
  }

  return context
}

export default GameplayProvider
